import {
  RefreshCw,
  AlertCircle,
  Moon,
  Sun,
} from 'lucide-react'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import {
  MetricCard,
  QuickActions,
  RecentActivity,
  GettingStarted,
} from '@/components/dashboard'
import { ActivityOverview } from '@/components/dashboard/activity-overview'
import { useDashboardMetrics } from '@/hooks/use-dashboard-metrics'
import { useReportSummary } from '@/hooks/use-reports'
import { useThemeStore } from '@/stores/theme-store'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'

function getTimeGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

function getFirstName(displayName: string): string {
  return displayName.split(' ')[0]
}

/**
 * Compute trend percentage from current vs previous period totals.
 * Returns undefined when previous period has no data (new tenant).
 */
function computeTrend(
  current: number | undefined,
  previous: number | undefined
): { value: number; label: string } | undefined {
  if (previous === undefined || previous === 0) return undefined
  if (current === undefined) return undefined
  const diff = ((current - previous) / previous) * 100
  return { value: Math.round(diff * 10) / 10, label: 'from last week' }
}

/**
 * Format last activity timestamp to human-readable text
 */
function getLastActivityText(timestamp: string | null | undefined): string {
  if (!timestamp) return 'No recent activity'

  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minutes ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hours ago`

  return date.toLocaleDateString()
}

/**
 * Dashboard page component
 * Main overview page showing KPIs, quick actions, and activity
 */
export function DashboardPage() {
  const { data: metrics, isLoading, isError, error, refetch } = useDashboardMetrics()
  const { theme, toggleTheme } = useThemeStore()
  const { user } = useAuthStore()

  // Fetch current and previous 7-day summaries for trend calculation
  const currentSummary = useReportSummary({ relativeDays: 7, groupBy: 'action' })
  const previousSummary = useReportSummary({
    startDate: new Date(Date.now() - 14 * 86400000).toISOString(),
    endDate: new Date(Date.now() - 7 * 86400000).toISOString(),
    groupBy: 'action',
  })

  const activityTrend = computeTrend(
    currentSummary.data?.total,
    previousSummary.data?.total
  )

  const displayName = user?.displayName ?? 'there'
  const greeting = `${getTimeGreeting()}, ${getFirstName(displayName)}`

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Personalized Greeting */}
        <div>
          <h2 className="text-lg font-semibold">{greeting}</h2>
          <p className="text-sm text-muted-foreground">
            Here&apos;s an overview of your policy administration environment
          </p>
        </div>

        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load metrics:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Metric Cards */}
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Attributes"
            value={metrics?.attributeCount ?? 0}
            description="Defined attribute schemas"
            isLoading={isLoading}
            href="/pac/attributes"
          />
          <MetricCard
            title="Active Policies"
            value={metrics?.activeCount ?? 0}
            description="Published policies"
            isLoading={isLoading}
            href="/pac/policies?status=ACTIVE"
          />
          <MetricCard
            title="Policy Drafts"
            value={metrics?.draftCount ?? 0}
            description="Policies in progress"
            isLoading={isLoading}
            href="/pac/policies?status=DRAFT"
          />
          <MetricCard
            title="Recent Activity"
            value={metrics?.recentActivityCount ?? 0}
            description={getLastActivityText(metrics?.lastActivityTimestamp)}
            trend={activityTrend}
            isLoading={isLoading}
            href="/pac/analytics?tab=audit-logs"
          />
        </div>

        {/* Activity Overview */}
        <ActivityOverview />

        {/* Quick Actions and Recent Activity */}
        <div className="grid gap-4 lg:grid-cols-2">
          <QuickActions />
          <RecentActivity />
        </div>

        {/* Getting Started Guide */}
        <GettingStarted
          attributeCount={metrics?.attributeCount}
          policyCount={metrics?.policyCount}
        />
      </div>
    </>
  )
}

export default DashboardPage
